'use client'

import { useCallback } from 'react'
import { useSnackbar } from 'notistack'
import { useTranslations } from '@/lib/next-shim'

import { useUpdateMyInfosMutation } from '@/core/graphql/hooks'
import type { UpdatePatientInput } from '@/core/graphql/types'

/**
 * Wraps the updateMyInfos mutation with the shared success/error snackbar
 * feedback used by the profile sections that don't need the identity gate
 * (primary care, insurance).
 */
export function useProfileSave() {
  const t = useTranslations()
  const { enqueueSnackbar } = useSnackbar()
  const [updateInfos, { loading }] = useUpdateMyInfosMutation()

  const save = useCallback(
    async (payload: Partial<UpdatePatientInput>) => {
      try {
        await updateInfos({ variables: { payload } })
        enqueueSnackbar(t('page.AccountSettings.profile.snackbar.saveSuccess'), { variant: 'success' })
      } catch (e) {
        enqueueSnackbar(t('page.AccountSettings.profile.snackbar.saveError'), { variant: 'error' })
      }
    },
    [updateInfos, enqueueSnackbar, t]
  )

  return [save, loading] as const
}
